/**
 * Aura Recommendations: portfolio-aware, vision-driven cards with
 * Portfolio Fit, Vision Match, Projected Profit, Profit Routing CTA, and FI Impact.
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LightBulbIcon,
  ArrowPathIcon,
  ChartBarIcon,
  FlagIcon,
  CurrencyDollarIcon,
  ArrowRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import TalkToAdvisorButton from './advice/TalkToAdvisorButton';
import { useAdviceSync } from '../context/AdviceSyncContext';

const normalizeVisionMatch = (visionMatch) => {
  if (!visionMatch) return [];
  if (Array.isArray(visionMatch)) return visionMatch;
  if (typeof visionMatch === 'string') {
    return [{ vision_id: 'vision', title: visionMatch, progress_pct: null }];
  }
  if (typeof visionMatch === 'object') {
    return [visionMatch];
  }
  return [];
};

const asText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
};

const ProfitRoutingModal = ({ open, onClose, recommendationId, symbol, title, visions, onSaved }) => {
  const [allocations, setAllocations] = useState([]);
  const [unallocated, setUnallocated] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && visions?.length) {
      setAllocations(visions.map((v) => ({ vision_id: v.vision_id, title: v.title, percentage: 0 })));
      setUnallocated(100);
    }
  }, [open, visions]);

  const totalAllocated = allocations.reduce((s, a) => s + (Number(a.percentage) || 0), 0);
  const totalPct = totalAllocated + (Number(unallocated) || 0);

  const setVisionPct = (visionId, pct) => {
    const n = Math.max(0, Math.min(100, Number(pct) || 0));
    setAllocations((prev) => {
      const next = prev.map((a) => (a.vision_id === visionId ? { ...a, percentage: n } : a));
      const total = next.reduce((s, a) => s + (Number(a.percentage) || 0), 0);
      setUnallocated(Math.max(0, 100 - total));
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await axios.post('/api/aura/profit-routing', {
        recommendation_id: recommendationId || undefined,
        symbol: symbol || undefined,
        allocations: allocations.filter((a) => (Number(a.percentage) || 0) > 0).map((a) => ({ vision_id: a.vision_id, percentage: Number(a.percentage) })),
        unallocated_percentage: Math.round(unallocated * 10) / 10,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save routing');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div
        className="surface-panel max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-hairline">
          <h3 className="text-lg font-display font-semibold text-primary">Choose where profits go</h3>
          <button onClick={onClose} className="btn-ghost p-1">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {title && <p className="text-secondary text-sm mb-4">For: {title}</p>}
          <p className="text-faint text-xs mb-3">Allocate profit % to Visions (remaining = unallocated / reinvest).</p>
          {visions?.length === 0 ? (
            <p className="text-pine text-sm">Create a Vision to unlock profit routing.</p>
          ) : (
            <>
              {allocations.map((a) => (
                <div key={a.vision_id} className="flex items-center gap-3 mb-3">
                  <label className="flex-1 text-primary text-sm truncate">{a.title}</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={a.percentage}
                    onChange={(e) => setVisionPct(a.vision_id, e.target.value)}
                    className="input-editorial w-20 text-sm py-1"
                  />
                  <span className="text-faint text-sm">%</span>
                </div>
              ))}
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-hairline">
                <span className="flex-1 text-secondary text-sm">Unallocated (reinvest / general)</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={unallocated}
                  readOnly
                  className="input-editorial w-20 text-sm py-1 text-secondary"
                />
                <span className="text-faint text-sm">%</span>
              </div>
            </>
          )}
          {error && <p className="text-danger text-sm mt-2">{error}</p>}
        </div>
        <div className="p-4 border-t border-hairline flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (visions?.length > 0 && totalPct > 100.01)}
            className="btn-primary disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save routing'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default function AgentNextBestActionsCard() {
  const { focusLine, refresh: refreshAdviceSync } = useAdviceSync();
  const [recommendations, setRecommendations] = useState([]);
  const [prompts, setPrompts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visions, setVisions] = useState([]);
  const [routingModal, setRoutingModal] = useState({ open: false, rec: null });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, visionsRes, ledgerRes] = await Promise.all([
        axios.get('/api/ai/next_best_actions'),
        axios.get('/api/visions').catch(() => ({ data: { visions: [] } })),
        axios.get('/api/advice/recommendations').catch(() => ({ data: { recommendations: [] } })),
      ]);
      const aiRecs = res.data?.recommendations || res.data?.actions || [];
      const ledgerRecs = ledgerRes.data?.recommendations || [];
      const merged = aiRecs.length ? aiRecs : ledgerRecs;
      setRecommendations(merged);
      setPrompts(res.data?.prompts || {});
      setVisions(visionsRes.data?.visions || []);
      await refreshAdviceSync();
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError('Could not load recommendations');
      setRecommendations([]);
      setPrompts({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading && recommendations.length === 0) {
    return (
      <motion.div
        className="surface-panel p-5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-3 mb-3">
          <LightBulbIcon className="w-5 h-5 text-pine" />
          <h3 className="text-lg font-display font-semibold text-primary">Aura recommends</h3>
        </div>
        <div className="flex items-center gap-2 text-secondary text-sm">
          <div className="w-4 h-4 border-2 border-pine border-t-transparent rounded-full animate-spin" />
          Analyzing your portfolio and visions...
        </div>
      </motion.div>
    );
  }

  if (error && recommendations.length === 0) {
    return (
      <motion.div
        className="surface-panel p-5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <LightBulbIcon className="w-5 h-5 text-pine" />
            <h3 className="text-lg font-display font-semibold text-primary">Aura recommends</h3>
          </div>
          <button onClick={fetchData} className="text-xs text-pine hover:underline">
            Retry
          </button>
        </div>
        <p className="text-secondary text-sm">{error}</p>
      </motion.div>
    );
  }

  const showNoPortfolio = prompts.no_portfolio && recommendations.length === 0;
  const showNoVisions = prompts.no_visions;

  return (
    <>
      <motion.div
        className="surface-panel p-5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <LightBulbIcon className="w-5 h-5 text-pine" />
              <h3 className="text-lg font-display font-semibold text-primary">Aura recommends</h3>
            </div>
            {focusLine && (
              <p className="text-label text-secondary mt-1 ml-8 line-clamp-2">{focusLine}</p>
            )}
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="icon-btn disabled:opacity-50"
            title="Refresh"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {showNoPortfolio && (
          <p className="text-secondary text-sm mb-4 p-3 rounded-lg bg-pine-tint border border-hairline">
            {prompts.no_portfolio}
          </p>
        )}
        {showNoVisions && recommendations.length > 0 && (
          <p className="text-pine text-sm mb-4 p-3 rounded-lg bg-pine-tint border border-hairline">
            {prompts.no_visions}
          </p>
        )}

        <ul className="space-y-4">
          {recommendations.length === 0 && (
            <li className="text-secondary text-sm p-4 rounded-lg bg-bone border border-hairline">
              No personalized actions yet. Add cashflow data or connect a portfolio and Aura will
              suggest your next move.
            </li>
          )}
          {recommendations.slice(0, 5).map((rec, index) => {
            const recId = rec.recommendation_id || rec.data?.vision_id || rec.data?.symbol || index;
            const hasInvestableType = ['vision_contribution', 'diversify_bonds', 'growth_for_vision'].includes(rec.type);
            const visionMatches = normalizeVisionMatch(rec.vision_match);
            const projectedProfit =
              rec.projected_profit && typeof rec.projected_profit === 'object'
                ? rec.projected_profit
                : null;
            return (
              <li
                key={recId}
                className="p-4 rounded-lg bg-bone border border-hairline flex flex-col gap-3"
              >
                <div>
                  <span className="font-medium text-primary text-sm block">{asText(rec.title) || 'Recommendation'}</span>
                  {asText(rec.reason) && (
                    <span className="text-xs text-secondary block mt-1">{asText(rec.reason)}</span>
                  )}
                </div>

                {rec.portfolio_fit_score != null && (
                  <div className="flex items-center gap-2 text-xs">
                    <ChartBarIcon className="w-4 h-4 text-pine" />
                    <span className="text-secondary">Portfolio Fit:</span>
                    <span className="text-pine font-medium">{rec.portfolio_fit_score}/100</span>
                    {asText(rec.portfolio_fit_reason) && (
                      <span className="text-faint">— {asText(rec.portfolio_fit_reason)}</span>
                    )}
                  </div>
                )}

                {visionMatches.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <FlagIcon className="w-4 h-4 text-brass" />
                    <span className="text-secondary">Vision match:</span>
                    {visionMatches.map((v) => (
                      <span
                        key={v.vision_id || v.title}
                        className="topic-tag"
                      >
                        {asText(v.title) || 'Vision'}
                        {v.progress_pct != null ? ` (${Math.round(Number(v.progress_pct))}%)` : ''}
                      </span>
                    ))}
                  </div>
                )}

                {projectedProfit &&
                  (projectedProfit['1y'] != null ||
                    projectedProfit['3y'] != null ||
                    projectedProfit['5y'] != null) && (
                  <div className="flex items-center gap-2 text-xs text-secondary">
                    <CurrencyDollarIcon className="w-4 h-4" />
                    <span>Projected return (est.):</span>
                    {[['1y', '1y'], ['3y', '3y'], ['5y', '5y']].map(([k, label]) =>
                      projectedProfit[k] != null ? (
                        <span key={k}>{label} ~{projectedProfit[k]}%</span>
                      ) : null
                    )}
                  </div>
                )}

                {asText(rec.fi_impact) && (
                  <p className="text-xs text-pine flex items-center gap-1">
                    <ArrowRightIcon className="w-3.5 h-3.5" />
                    {asText(rec.fi_impact)}
                  </p>
                )}

                {hasInvestableType && (
                  <button
                    type="button"
                    onClick={() => setRoutingModal({ open: true, rec })}
                    className="self-start mt-1 btn-secondary text-xs py-1.5 px-3"
                  >
                    Choose where profits go
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <TalkToAdvisorButton
          userQuestion={
            recommendations[0]?.title
              ? `${recommendations[0].title} — discuss with an advisor`
              : 'Discuss my recommendations with an advisor'
          }
          surface="nba"
          framing={recommendations[0]?.title || ''}
          compact
        />
      </motion.div>

      <AnimatePresence>
        <ProfitRoutingModal
          open={routingModal.open}
          onClose={() => setRoutingModal({ open: false, rec: null })}
          recommendationId={routingModal.rec?.recommendation_id}
          symbol={routingModal.rec?.data?.symbol}
          title={routingModal.rec?.title}
          visions={visions}
          onSaved={fetchData}
        />
      </AnimatePresence>
    </>
  );
}
